/**
 * The last good copy of each opened procedure (portal view JSON), kept in localStorage so the
 * procedure still opens with no signal, even before the service worker has cached anything.
 * The service worker caches the same JSON (NetworkFirst) and the photos (query string stripped);
 * see vite.config.ts.
 *
 * Signed media links inside a saved copy expire after about an hour. Offline that does not matter
 * (the photos answer from the service worker cache by path); online the app refetches to renew them.
 */
import type { ProcedureMinistryOption, ProcedurePortalView, ProcedurePublicSettingsView } from '@actschurch/ccms-sdk';
import type { PortalMode } from './queryKeys';
import { readJson, removeKey, writeJson } from './storage';

const INDEX_KEY = 'procedures.views';
const MAX_VIEWS = 15;
const viewKey = (mode: PortalMode, code: string) => `procedures.view.${mode}.${code.toLowerCase()}`;
const SETTINGS_KEY = 'procedures.settings';
const MINISTRIES_KEY = 'procedures.ministries';

export interface SavedView {
  savedAt: number;
  view: ProcedurePortalView;
}

export function saveViewSnapshot(mode: PortalMode, code: string, view: ProcedurePortalView): void {
  const key = viewKey(mode, code);
  const ok = writeJson(key, { savedAt: Date.now(), view } satisfies SavedView);
  const index = (readJson<string[]>(INDEX_KEY) ?? []).filter((k) => k !== key);
  index.unshift(key);
  // Keep the newest few; drop the oldest (and retry once when the quota was the problem).
  const dropped = index.splice(MAX_VIEWS);
  dropped.forEach(removeKey);
  writeJson(INDEX_KEY, index);
  if (!ok && index.length > 1) {
    const oldest = index.pop();
    if (oldest) removeKey(oldest);
    writeJson(INDEX_KEY, index);
    writeJson(key, { savedAt: Date.now(), view } satisfies SavedView);
  }
}

/** The saved copy for this mode, falling back to the other mode's copy (same published snapshot). */
export function getViewSnapshot(mode: PortalMode, code: string): SavedView | null {
  const own = readJson<SavedView>(viewKey(mode, code));
  if (own?.view) return own;
  const other = readJson<SavedView>(viewKey(mode === 'member' ? 'public' : 'member', code));
  return other?.view ? other : null;
}

export function saveSettingsSnapshot(settings: ProcedurePublicSettingsView): void {
  writeJson(SETTINGS_KEY, settings);
}

export function getSettingsSnapshot(): ProcedurePublicSettingsView | null {
  return readJson<ProcedurePublicSettingsView>(SETTINGS_KEY);
}

export interface RecentView {
  code: string;
  title: string;
  locationName?: string | null;
  savedAt: number;
}

/** Procedures opened on this phone, newest first (for the directory when there is no signal). */
export function getRecentViews(limit = 8): RecentView[] {
  const index = readJson<string[]>(INDEX_KEY) ?? [];
  const seen = new Set<string>();
  const out: RecentView[] = [];
  for (const key of index) {
    const saved = readJson<SavedView>(key);
    if (!saved?.view) continue;
    const code = saved.view.code.toLowerCase();
    if (seen.has(code)) continue;
    seen.add(code);
    out.push({ code: saved.view.code, title: saved.view.title, locationName: saved.view.locationName, savedAt: saved.savedAt });
    if (out.length >= limit) break;
  }
  return out;
}

export function saveMinistriesSnapshot(list: ProcedureMinistryOption[]): void {
  writeJson(MINISTRIES_KEY, list);
}

export function getMinistriesSnapshot(): ProcedureMinistryOption[] | null {
  const list = readJson<ProcedureMinistryOption[]>(MINISTRIES_KEY);
  return Array.isArray(list) ? list : null;
}
